/**
 * Purchase Orders Page.
 *
 * Lists all purchase orders with status filtering and pagination.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Plus, Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { usePurchaseOrders, useCreatePO } from "@/features/procurement/api";
import { PO_STATUSES } from "@/types/purchase-order";
import { toast } from "@/stores/toastStore";
import Badge from "@/components/ui/Badge";
import BulkActionBar from "@/components/ui/BulkActionBar";
import BulkResultPanel from "@/components/BulkResultPanel";
import { useBulkRun, type BulkOutcome } from "@/features/bulk/api";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card";
import ConfirmDeleteDialog from "@/components/ui/ConfirmDeleteDialog";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

const statusVariant: Record<
  string,
  "default" | "success" | "warning" | "danger" | "info"
> = {
  Draft: "default",
  Submitted: "info",
  "In-Transit": "warning",
  "Partially Received": "warning",
  Received: "success",
  Cancelled: "danger",
};

function PurchaseOrdersPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const createPOMutation = useCreatePO();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined,
  );
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const { data, isLoading } = usePurchaseOrders({ page, status: statusFilter });

  // One request, and the server reports every row. The loop this replaces fired
  // one `DELETE ?force=true` per selected row and discarded every failure, so a
  // run that half worked said "37 of 50 deleted" and left the user to find out
  // which thirteen by looking at the list. `cascade` is no longer implied:
  // purchase orders with related records come back named, in the panel.
  const bulkRun = useBulkRun();
  const [bulkOutcome, setBulkOutcome] = useState<BulkOutcome | null>(null);

  const runBulk = (operation: string, params: Record<string, unknown> = {}) => {
    const ids = [...selected];
    bulkRun.mutate(
      { operation, selection: { ids }, params },
      {
        onSuccess: (outcome) => {
          qc.invalidateQueries({ queryKey: ["purchase-orders"] });
          setSelected(new Set());
          setBulkOutcome(outcome.failed > 0 ? outcome : null);
          if (outcome.failed === 0) toast.success(outcome.message);
          else if (outcome.succeeded === 0) toast.error(outcome.message);
          else toast.warning(outcome.message);
        },
        onError: (error: unknown) => {
          const detail =
            (error as { response?: { data?: { detail?: string } } })?.response
              ?.data?.detail ?? "That could not be done.";
          toast.error(detail);
        },
      },
    );
  };

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(v);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Purchase Orders
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Manage procurement and incoming inventory.
          </p>
        </div>
        <Button
          isLoading={createPOMutation.isPending}
          onClick={async () => {
            try {
              const po = await createPOMutation.mutateAsync({});
              navigate(`/purchase-orders/${po.id}`);
            } catch {
              toast.error("Failed to create purchase order");
            }
          }}
        >
          <Plus className="h-4 w-4" /> New PO
        </Button>
      </div>

      {/* Status Filters */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => {
            setStatusFilter(undefined);
            setPage(1);
          }}
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${!statusFilter ? "bg-blue-100 text-[var(--color-interactive-primary)]" : "bg-[var(--surface-sunken)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]/80"}`}
        >
          All
        </button>
        {PO_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => {
              setStatusFilter(s);
              setPage(1);
            }}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${statusFilter === s ? "bg-blue-100 text-[var(--color-interactive-primary)]" : "bg-[var(--surface-sunken)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]/80"}`}
          >
            {s}
          </button>
        ))}
      </div>

      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : data && data.items.length > 0 ? (
        <>
          <div className="space-y-2 stagger-children">
            {data.items.map((po) => {
              const lineCount = po.line_count ?? po.line_items?.length ?? 0;
              const hasShipping = po.shipping_cost > 0;
              return (
                <Card
                  key={po.id}
                  padding="none"
                  className="cursor-pointer hover-lift"
                  onClick={() => navigate(`/purchase-orders/${po.id}`)}
                >
                  <div className="flex items-stretch">
                    <div
                      className="flex items-center pl-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(po.id)}
                        onChange={() => toggleSelect(po.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="shrink-0 rounded border-[var(--border-default)]"
                      />
                    </div>
                    <div className="flex-1 p-4">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-semibold text-brand-600">
                          {po.po_number}
                        </span>
                        <Badge variant={statusVariant[po.status] || "default"}>
                          {po.status}
                        </Badge>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--text-secondary)]">
                        <span>
                          {lineCount} item{lineCount !== 1 ? "s" : ""}
                        </span>
                        {po.order_date && (
                          <span>
                            Ordered{" "}
                            {new Date(po.order_date).toLocaleDateString()}
                          </span>
                        )}
                        {po.expected_delivery_date && (
                          <span>
                            Expected{" "}
                            {new Date(
                              po.expected_delivery_date,
                            ).toLocaleDateString()}
                          </span>
                        )}
                        {po.received_date && (
                          <span>
                            Received{" "}
                            {new Date(po.received_date).toLocaleDateString()}
                          </span>
                        )}
                        {po.notes && (
                          <span className="truncate max-w-[200px] italic">
                            {po.notes}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="hidden sm:flex shrink-0 items-center border-l border-[var(--color-border-light)] bg-[var(--color-surface-tertiary)]">
                      <div className="w-[110px] text-right px-3 py-4">
                        <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                          Subtotal
                        </p>
                        <p className="text-sm tabular-nums text-[var(--text-secondary)] mt-0.5">
                          {formatCurrency(po.subtotal)}
                        </p>
                      </div>
                      <div className="w-[100px] text-right px-3 py-4">
                        <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                          Shipping
                        </p>
                        <p className="text-sm tabular-nums text-[var(--text-secondary)] mt-0.5">
                          {hasShipping ? formatCurrency(po.shipping_cost) : "—"}
                        </p>
                      </div>
                      <div className="w-[100px] text-right px-3 py-4">
                        <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                          Tax
                        </p>
                        <p className="text-sm tabular-nums text-[var(--text-secondary)] mt-0.5">
                          {po.tax > 0 ? formatCurrency(po.tax) : "—"}
                        </p>
                      </div>
                      <div className="w-[110px] text-right px-3 py-4 pr-5">
                        <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                          Total
                        </p>
                        <p className="text-sm tabular-nums font-bold text-[var(--text-primary)] mt-0.5">
                          {formatCurrency(po.total)}
                        </p>
                      </div>
                    </div>
                    <div
                      className="flex items-center gap-1 shrink-0 pr-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => setDeleteTarget(po.id)}
                        className="p-1.5 text-[var(--text-tertiary)] hover:text-[var(--text-danger)] transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
          {data.pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-[var(--text-secondary)]">
                Page {data.page} of {data.pages} ({data.total} total)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page >= data.pages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <FileText />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No purchase orders found
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Create your first purchase order to get started.
            </p>
            <Button
              className="mt-4"
              isLoading={createPOMutation.isPending}
              onClick={async () => {
                try {
                  const po = await createPOMutation.mutateAsync({});
                  navigate(`/purchase-orders/${po.id}`);
                } catch {
                  toast.error("Failed to create purchase order");
                }
              }}
            >
              <Plus className="h-4 w-4" /> New PO
            </Button>
          </div>
        </Card>
      )}

      <ConfirmDeleteDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        apiPath="/api/v1/purchase-orders"
        entityId={deleteTarget || ""}
        queryKey="purchase-orders"
      />

      <BulkResultPanel
        outcome={bulkOutcome}
        onClose={() => setBulkOutcome(null)}
        noun="purchase orders"
      />

      <BulkActionBar
        selectedCount={selected.size}
        onClearSelection={() => setSelected(new Set())}
        actions={[
          {
            label: "Cancel Selected",
            onClick: () => runBulk("purchase_order.cancel"),
            isLoading: bulkRun.isPending,
          },
          {
            label: "Delete Selected",
            onClick: () => runBulk("purchase_order.delete", { cascade: true }),
            variant: "danger",
            isLoading: bulkRun.isPending,
          },
        ]}
      />
    </div>
  );
}

export default PurchaseOrdersPage;
